import { Link } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { cn } from '../lib/utils';
import { TabCount } from './TabCount';
import { PHONE_HEADER_HEIGHT } from './PhoneHeader';

/**
 * A page's toolbar on a phone: one compact panel, the same on every page,
 * sticky under the phone bar (PhoneHeader) so its tabs and actions are always
 * in reach.
 *
 *   tabs     segmented, full width: the desktop's own Tabs. More than four
 *            wrap into rows of four (Settings has seven), so every one
 *            stays in sight instead of scrolling sideways out of it
 *   actions  a row of cells split by hairlines; the primary action is the
 *            dark cell, last. An action is { label, icon, onClick | to,
 *            primary, disabled }, or { key, node } for a control of its
 *            own: a job's status picker (PHONE_CELL_CONTROL)
 *
 * No search or filters on a phone: the panel is for moving around and
 * adding things.
 *
 * Every row is 48px tall (h-12). The tabs keep the desktop's 36px pills, so
 * their track pads them by 6px; a pill's curve is 18px, so the panel's 24px
 * corners run parallel to the selected tab. Tailwind needs each class
 * written out in full, hence the literal rounded-*-[24px] below.
 *
 * Pages render this instead of their desktop toolbar when useIsPhone() is
 * true. Used by the Dashboard, Jobs, a job, Equipment, Clients and Settings.
 */
export function PhonePanel({ tabs, actions = [] }) {
  const hasActions = actions.length > 0;
  const wraps = (tabs?.items.length || 0) > 4;
  return (
    // The background fills the gutters either side and a strip below, so the
    // page can't show around the panel as it scrolls under it. -mb-2 gives
    // the strip's height back, so the page's spacing below is unchanged
    <div
      className="sticky z-30 -mx-4 px-4 pb-2 -mb-2 bg-background"
      style={{ top: PHONE_HEADER_HEIGHT }}
      data-testid="phone-panel"
    >
      {tabs && (
        <Tabs value={tabs.value} onValueChange={tabs.onChange}>
          <TabsList
            className={cn(
              'w-full p-1.5 rounded-none',
              wraps ? 'grid grid-cols-4 gap-1 h-auto' : 'flex h-12',
              hasActions ? 'rounded-t-[24px]' : 'rounded-[24px]'
            )}
          >
            {tabs.items.map((item) => (
              <TabsTrigger key={item.value} value={item.value} className={wraps ? 'px-1' : 'flex-1 px-2'} data-testid={item.testId}>
                {item.label}
                <TabCount count={item.count} className="ml-1.5" />
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}
      {hasActions && (
        <div className={cn('flex h-12 bg-card', tabs ? 'rounded-b-[24px]' : 'rounded-[24px]')}>
          {actions.map((action, i) => (
            <PhoneAction
              key={action.key || action.label}
              {...action}
              divided={i > 0}
              first={i === 0}
              last={i === actions.length - 1}
              topRounded={!tabs}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** A select (or other control) filling an action cell, flush like the rest. */
export const PHONE_CELL_CONTROL =
  'h-full w-full rounded-none border-0 bg-transparent hover:bg-mist hover:border-transparent px-4 text-sm';

function PhoneAction({ label, icon: Icon, onClick, to, node, primary = false, disabled = false, divided, first, last, topRounded, testId }) {
  const className = cn(
    'flex-auto h-full px-3 flex items-center justify-center gap-1.5 text-sm font-medium whitespace-nowrap transition-colors',
    primary ? 'bg-inverse text-inverse-foreground hover:opacity-95' : 'text-foreground hover:bg-mist',
    divided && !primary && 'border-l border-line',
    first && 'rounded-bl-[24px]',
    last && 'rounded-br-[24px]',
    first && topRounded && 'rounded-tl-[24px]',
    last && topRounded && 'rounded-tr-[24px]',
    disabled && 'opacity-45 pointer-events-none',
    node && 'p-0 hover:bg-transparent overflow-hidden'
  );
  if (node) return <div className={className}>{node}</div>;
  const body = (
    <>
      {Icon && <Icon className="w-4 h-4" />}
      {label}
    </>
  );
  return to && !disabled ? (
    <Link to={to} className={className} data-testid={testId}>{body}</Link>
  ) : (
    <button type="button" onClick={onClick} disabled={disabled} className={className} data-testid={testId}>{body}</button>
  );
}
